import { computed, reactive, ref } from 'vue';
import { useAcreditacionPrintLimit } from '@/composables/admin/events/useAcreditacionPrintLimit';

/**
 * Estado y comportamiento del formulario de acreditación.
 *
 * Vive en un composable, y no en el componente, porque es lógica de negocio: carga
 * del evento y de las puertas, textos por defecto, medición de los campos impresos y
 * la decisión de bloquear el guardado. El componente sólo pinta y recolecta.
 *
 * Los `editores` llegan como refs (mismo patrón que `usePrintBoxFit`), de modo que el
 * composable no depende del DOM ni del contrato interno de `QuillEditor`: sólo llama
 * a `flushPrintFit()` si el editor lo expone.
 *
 * @param {{
 *   props: Object,
 *   emit: Function,
 *   store: Object,
 *   config: Object,
 *   validar: () => Promise<boolean>,
 *   editores: Record<string, import('vue').Ref<Object|null>>
 * }} params
 */
export function useAcreditacionForm({ props, emit, store, config, validar, editores }) {
    const { camposConLimite, printBoxPara, hayContenidoQueNoEntra } = useAcreditacionPrintLimit();

    const loading = ref(false);
    const terminado = ref(false);
    const puertas = ref(null);
    const allAccess = ref(false);

    /**
     * Fallo de la carga inicial, con mensaje para el usuario. Sin esto, un fallo
     * dejaba `puertas` en `null`: el selector (que es obligatorio) no se pintaba,
     * la validación pasaba y se podía guardar una acreditación incompleta.
     */
    const errorCarga = ref(null);

    /** El guardado sólo está disponible con la carga inicial completa y sin fallos. */
    const puedeGuardar = computed(() => !loading.value && !errorCarga.value);

    /*
     * `condiciones` se declara aunque venga vacía: en Vue 2 una propiedad que no
     * existe en el objeto no es reactiva, y `getEvento` la rellena al cargar.
     */
    const localAcreditacion = reactive({
        tipoDocumento: 'DNI',
        condiciones: undefined,
        ...props.acreditacion
    });

    const teatro = store.getters.getTeatro;

    const textoCondiciones = `
                <p>La presente acreditación sólo habilita al titular a acceder, transitar, ocupar y trabajar en las zonas dentro del recinto que se indique en la acreditación y a hacer uso de ella para el fin que se le ha otorgado. Las acreditaciones son personales e intransferibles.</p>

                <p>La acredicación es propiedad de ${teatro.nombre.toUpperCase()}, que se reserva el derecho de su anulación o retirada bajo sus propios criterios. El uso indebido de la misma supondrá su retirada y la imposibilidad del titular de participar en otros eventos.</p>

                ${teatro.hash !== 'QyK2Lux6tM7vnfKINhef' ? `<p>El titular no podrá grabar, reproducir o comunicar públicamente sonidos, imágenes, vídeos, datos y/o estadísticas de los eventos que sucedan en el recinto para los que la acreditación da acceso, salvo autorización de la empresa promotora.</p>` : ''}

                <p>Sus datos personales serán tratados a los efectos exclusivos de identificación y control de acceso al recinto.</p>
    `;

    const cargarPuertas = async() => {
        const params = {
            filters: {},
            evento_id: props.evento_id
        };

        const res = await store.dispatch(config.getVuexAlias('back.events.acreditaciones.puertas-evento'), params);

        puertas.value = res.data.data;

        if (puertas.value.length === localAcreditacion.puertasSelect?.length) {
            allAccess.value = true;
        }
    };

    const cargarEvento = async() => {
        const params = {
            filters: {},
            evento_id: props.evento_id
        };

        const res = await store.dispatch(config.getVuexAlias('back.events.show'), params);

        /*
         * No se pisa lo que el usuario ya tenga cargado: un reintento tras un fallo
         * no debe descartar las condiciones que haya escrito mientras tanto.
         */
        if (!localAcreditacion.condiciones) {
            localAcreditacion.condiciones =
                props.acreditacion.condiciones ??
                res.data.data.condiciones_acreditaciones ??
                textoCondiciones;
        }
    };

    /**
     * Carga inicial: evento + puertas como UNA sola operación.
     *
     * 1. `loading` tiene que seguir activo hasta que terminen las DOS. Con un
     *    `finally` por carga, la primera en resolver apaga `loading` y el
     *    formulario se muestra a medio cargar; una respuesta tardía del evento
     *    podría además pisar las condiciones que el usuario ya esté editando.
     * 2. Ninguna carga debe dejar un rechazo sin gestionar: el fallo de cada una
     *    se registra aquí, así que ambas comparten el mismo contrato de fallo y
     *    un error no cancela la otra carga.
     *
     * Y si algo falla queda `errorCarga`, que bloquea el guardado hasta que un
     * reintento complete los datos: no se puede guardar con la carga a medias.
     */
    const cargarInicial = async() => {
        loading.value = true;
        errorCarga.value = null;

        const fallos = [];
        const cargas = [
            { origen: 'el evento', carga: cargarEvento() },
            { origen: 'las puertas', carga: cargarPuertas() }
        ];

        try {
            /*
             * Se captura cada carga por separado a propósito: `Promise.all` corta
             * en el primer rechazo y apagaría `loading` con la otra en vuelo.
             */
            await Promise.all(cargas.map(({ origen, carga }) => carga.catch(error => {
                console.error(`Error al cargar ${origen} de la acreditación`, error);
                fallos.push(origen);
            })));
        } finally {
            errorCarga.value = fallos.length
                ? 'No se pudieron cargar los datos necesarios del formulario. Vuelve a intentarlo.'
                : null;
            loading.value = false;
        }
    };

    const procesarEditoresQuill = () => {
        ['acceso_zonas', 'observaciones', 'mensaje', 'condiciones'].forEach(campo => {
            const editor = editores[campo]?.value;

            if (editor) {
                localAcreditacion[campo] = editor.processAndGetHTML();
            }
        });
    };

    /**
     * Resultados de ajuste del contenido actual, indexados por campo limitado.
     *
     * Fuerza la medición de cada editor: no vale el último valor calculado, que puede
     * ser anterior al contenido en pantalla si se guarda dentro de la ventana de
     * debounce.
     */
    const ajustesDeEditoresLimitados = async() => {
        const ajustes = {};

        await Promise.all(camposConLimite.map(async({ campo }) => {
            const editor = editores[campo]?.value;
            const ajuste = editor?.flushPrintFit ? await editor.flushPrintFit() : null;

            ajustes[campo] = ajuste;

            return ajuste;
        }));

        return ajustes;
    };

    const submit = async() => {
        if(!puedeGuardar.value) {
            return;
        }

        const isValid = await validar();

        if(!isValid) {
            return;
        }

        const ajustes = await ajustesDeEditoresLimitados();

        if(hayContenidoQueNoEntra(ajustes)) {
            return;
        }

        terminado.value = true;
        procesarEditoresQuill();
        localAcreditacion.all_access = allAccess.value;
        emit('terminado', localAcreditacion);
    };

    /*
     * Arranque equivalente al `created()` de la versión Options API: el cuerpo de
     * `setup` corre en el mismo punto del ciclo de vida (antes de `created`).
     */
    cargarInicial();

    if (teatro.hash === 'QyK2Lux6tM7vnfKINhef' && !localAcreditacion.observaciones) {
        localAcreditacion.observaciones = `<p>La acreditación deberá ser devuelta a la finalización del partido en la Sala de Prensa del Estadio Salto del Caballo.</p>`;
    }

    if (!localAcreditacion.mensaje) {
        if (teatro.hash === 'J1fKG9AarPuPNJ5hwY0t') {
            localAcreditacion.mensaje = `<p>Acreditaciones confirmadas.</p>
                        </br>
                        <p>Podrá recoger su acreditación desde 2 horas antes del partido en el punto AP indicado en el mapa adjunto. La persona de accesos le informará en qué cabina o pupitre ubicarse en caso de ser redactor o locutor. En caso de ser fotógrafo deberá bajar a planta -2 desde donde accederá a terreno de juego.</p>
                        <p>Les recordamos que no se pueden retransmitir imágenes de vídeo en directo ni semidirecto.</p>
                        <p>Le recordamos que las acreditaciones son nominativas e intransferibles. Además, solo tendrá validez para este encuentro y estará obligado a devolverla al final del partido en el mostrador junto a sala de prensa.</p>
                        </br>
                        <p>Un saludo.</p>`;
        } else if (teatro.hash === 'WBssBFDFMOEFHU3Jj4fT') {
            localAcreditacion.mensaje = `<p>La presente acreditación habilita exclusivamente a su titular para acceder, transitar, ocupar y desarrollar su labor profesional en las zonas del recinto que se indiquen en la misma. Las acreditaciones son personales e intransferibles.</p>
                        <br/>
                        <p>La acreditación es propiedad del Club Deportivo Lugo, que se reserva el derecho de su anulación o retirada bajo sus propios criterios. El uso indebido de la misma podrá suponer su retirada y la imposibilidad del titular de participar en futuros eventos organizados por el club.</p>
                        <br/>
                        <p>Sus datos personales serán tratados a los efectos exclusivos de identificación y control de acceso al recinto.</p>
                        <br/>
                        <p>Asimismo, se informa de que tanto los profesionales de prensa como los fotógrafos deberán recoger sus acreditaciones y, en su caso, los petos correspondientes en las taquillas del estadio. El acceso al recinto se realizará a través de las puertas de T4, ubicadas junto a dichas taquillas.</p>`;
        }
    }

    return {
        loading,
        terminado,
        puertas,
        allAccess,
        localAcreditacion,
        errorCarga,
        puedeGuardar,
        printBoxPara,
        reintentarCarga: cargarInicial,
        submit
    };
}
